import React, { useRef, useLayoutEffect } from 'react';

interface IconProps {
  /** Full FontAwesome class string, e.g. "fal fa-xmark" or "fas fa-volume-high". */
  icon: string;
  /** Extra class(es) on the wrapper <span> (spacing, etc.). */
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  title?: string;
  role?: string;
  ariaLabel?: string;
  ariaHidden?: boolean;
  id?: string;
}

/**
 * VI-725 — React-safe FontAwesome icon for the v2 UI theme.
 *
 * The v2 theme renders FA as SVG-JS: a MutationObserver REPLACES each `<i>` with an inline
 * `<svg>`. That breaks React whenever the `<i>` carries an `onClick` (handler lost with the node)
 * or a toggling className (the swapped `<svg>` never updates).
 *
 * This component keeps React managing only a stable wrapper `<span>` and writes the `<i>` into it
 * imperatively. The theme's observer is then free to swap that `<i>` for an `<svg>`; when the icon
 * class changes we rewrite the span so the theme re-converts it. `onClick` lives on the span (never
 * replaced), so it always fires. Works at any weight (incl. `fal`) with no Font Awesome Pro package.
 *
 * `icon` is an app-controlled class string (never user input), so the innerHTML write is safe.
 */
const Icon: React.FC<IconProps> = ({
  icon,
  className,
  style,
  onClick,
  title,
  role,
  ariaLabel,
  ariaHidden,
  id,
}) => {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = `<i class="${icon}"></i>`;
    }
  }, [icon]);

  return (
    <span
      ref={ref}
      id={id}
      className={className}
      style={style}
      onClick={onClick}
      title={title}
      role={role ?? (onClick ? 'button' : undefined)}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    />
  );
};

export default Icon;
